/**
 * Polilo line-icon set — 24px grid, 2px stroke, round caps and joins.
 * Shapes are declared as data so the icon component can render them without
 * an innerHTML sanitizer bypass.
 */
export type IconShape =
  | { k: 'path'; d: string }
  | { k: 'circle'; cx: number; cy: number; r: number }
  | { k: 'rect'; x: number; y: number; w: number; h: number; rx?: number }
  | { k: 'line'; x1: number; y1: number; x2: number; y2: number };

const p = (d: string): IconShape => ({ k: 'path', d });

export const ICONS = {
  // ── Navigation ──────────────────────────────────────────
  home: [p('M4 11 12 4l8 7'), p('M6 10v9h12v-9')],
  book: [
    p('M12 6.5C10.3 5.4 7.8 5 6 5v12c1.8 0 4.3.4 6 1.5 1.7-1.1 4.2-1.5 6-1.5V5c-1.8 0-4.3.4-6 1.5z'),
    p('M12 6.5v12'),
  ],
  bookmark: [p('M8 4h8a2 2 0 0 1 2 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 0 1 2-2z'), p('M9 9h6M9 12.5h4')],
  lexicon: [p('M5 4h9a2 2 0 0 1 2 2v14l-3-2-3 2V4'), p('M5 4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2'), p('M8 8h4')],
  compass: [{ k: 'circle', cx: 12, cy: 12, r: 9 }, p('m15.5 8.5-2 5-5 2 2-5z')],
  chart: [p('M4 19V5M4 19h16M8 16v-4M12 16V8M16 16v-6')],
  grid: [
    { k: 'rect', x: 4, y: 4, w: 7, h: 7, rx: 2 },
    { k: 'rect', x: 13, y: 4, w: 7, h: 7, rx: 2 },
    { k: 'rect', x: 4, y: 13, w: 7, h: 7, rx: 2 },
    { k: 'rect', x: 13, y: 13, w: 7, h: 7, rx: 2 },
  ],
  list: [p('M8 6h12M8 12h12M8 18h12'), p('M4 6h.01M4 12h.01M4 18h.01')],
  compassRose: [{ k: 'circle', cx: 12, cy: 12, r: 9 }, p('M12 7v10M7 12h10')],

  // ── Learning ────────────────────────────────────────────
  quiz: [
    { k: 'rect', x: 4, y: 3, w: 16, h: 18, rx: 3 },
    p('M9 11a3 3 0 1 1 4 2.8c-.8.4-1 .9-1 1.7'),
    p('M12 18h.01'),
  ],
  assignment: [p('M8 4h8a2 2 0 0 1 2 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 0 1 2-2z'), p('M9 9h6M9 12.5h4')],
  pencil: [p('M4 20h4L18.5 9.5a2 2 0 0 0-2.8-2.8L5 17.2z'), p('M14 7l3 3')],
  rocket: [
    p('M12 3c3 2 4.6 5.4 4.6 9.2L14 15h-4l-2.6-2.8C7.4 8.4 9 5 12 3z'),
    p('M12 8.5h.01'),
    p('M9.5 15l-2 4.5M14.5 15l2 4.5'),
  ],
  cup: [p('M4 8h11a3 3 0 0 1 0 6h-1'), p('M4 8h11v5a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4z'), p('M7 3v2M10 3v2')],
  basket: [p('M5 8h14l-1 11H6z'), p('M9 8a3 3 0 0 1 6 0')],
  notebook: [p('M5 5h10a2 2 0 0 1 2 2v12H7a2 2 0 0 1-2-2z'), p('M9 9h5M9 12h5')],
  cards: [{ k: 'rect', x: 4, y: 4, w: 16, h: 16, rx: 2.5 }, p('M8 9h8M8 12h8M8 15h5')],
  flashcard: [{ k: 'rect', x: 3, y: 6, w: 14, h: 12, rx: 2.5 }, p('M7 3h13a1 1 0 0 1 1 1v12')],
  trophy: [
    p('M8 4h8v5a4 4 0 0 1-8 0z'),
    p('M8 6H5.5a2.5 2.5 0 0 0 2.5 4M16 6h2.5a2.5 2.5 0 0 1-2.5 4'),
    p('M12 13v3M9 20h6l-.5-4h-5z'),
  ],
  target: [
    { k: 'circle', cx: 12, cy: 12, r: 8 },
    { k: 'circle', cx: 12, cy: 12, r: 4 },
    { k: 'circle', cx: 12, cy: 12, r: 1 },
  ],
  flame: [p('M12 3c.6 3 3.5 4 3.5 7a3.5 3.5 0 0 1-7 0c0-1 .4-1.8 1-2.5'), p('M12 21a6 6 0 0 0 6-6c0-3.5-3-5-4-8')],
  sparkle: [p('m12 4 1.6 4.4L18 10l-4.4 1.6L12 16l-1.6-4.4L6 10l4.4-1.6z'), p('M18 16.5 18.8 18.6 21 19.4l-2.2.8L18 22.5l-.8-2.3L15 19.4l2.2-.8z')],
  star: [p('m12 4 2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 9.7l5.4-.8z')],
  mountain: [p('M4 19h16'), p('M4 19l5.5-10 3 4.5 2-3L20 19z'), p('M12 4v3.5')],
  crown: [p('M4 18.5h16'), p('M4 18.5l1.6-9.5 3.9 4L12 6l2.5 7 3.9-4 1.6 9.5z')],
  sprout: [p('M12 21v-7'), p('M12 14c0-3 2.3-5.2 6-5.2 0 3-2.3 5.2-6 5.2z'), p('M12 15c0-2.6-2-4.6-5.2-4.6 0 2.6 2 4.6 5.2 4.6z')],
  flagGoal: [p('M5 21V4h11l-1.5 3.5L16 11H5'), p('M5 4V3')],
  camera2: [p('M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l2-3h8l2 3h3a2 2 0 0 1 2 2z'), { k: 'circle', cx: 12, cy: 13, r: 4 }],
  graduation: [p('M22 9 12 4 2 9l10 5 10-5z'), p('M6 11v5c0 1.4 2.7 2.6 6 2.6s6-1.2 6-2.6v-5')],
  panel: [{ k: 'rect', x: 3.5, y: 5, w: 17, h: 14, rx: 2 }, p('M7 9.5h10M7 13h6')],
  aim: [{ k: 'circle', cx: 12, cy: 12, r: 8 }, { k: 'circle', cx: 12, cy: 12, r: 3.5 }],

  // ── Coastal world ───────────────────────────────────────
  shell: [p('M12 3c5 0 8 3.5 8 8 0 5-4 10-8 10S4 16 4 11c0-4.5 3-8 8-8z'), p('M12 3v18M8 6l1 14M16 6l-1 14')],
  boat: [p('M4 15h16l-2 5H6zM12 4v11M12 4l6 4-6 2')],
  leaf: [p('M5 19c0-8 6-14 14-14 0 8-6 14-14 14zM5 19c3-3 6-5 9-6')],
  cart: [{ k: 'circle', cx: 9, cy: 20, r: 1.4 }, { k: 'circle', cx: 18, cy: 20, r: 1.4 }, p('M3 4h2l2.5 12h11l2-8H6')],
  hand: [
    p('M8 13V6a1.5 1.5 0 0 1 3 0v6M11 12V4.5a1.5 1.5 0 0 1 3 0V12M14 12V6a1.5 1.5 0 0 1 3 0v8a6 6 0 0 1-6 6h-1a5 5 0 0 1-4-2l-3-4a1.6 1.6 0 0 1 2.6-1.8L8 14'),
  ],
  hash: [p('M6 4v16M18 4v16M4 9h16M4 15h16')],
  lighthouse: [p('M9 8h6l1 12H8zM9 8l.5-4h5L15 8M4 6l3 1M20 6l-3 1M10 12h4')],
  trendUp: [p('M4 17l6-6 4 4 6-7'), p('M15 8h5v5')],
  radar: [p('M12 3l7.8 5.7-3 9.2H7.2l-3-9.2z'), p('M12 8l3.2 2.4-1.2 3.8H10l-1.2-3.8z')],

  // ── Speaking / audio ────────────────────────────────────
  mic: [{ k: 'rect', x: 9, y: 3, w: 6, h: 11, rx: 3 }, p('M5 11a7 7 0 0 0 14 0'), p('M12 18v3M9 21h6')],
  play: [p('m9 6 9 6-9 6z')],
  pause: [p('M9 5v14M15 5v14')],
  stop: [{ k: 'rect', x: 6, y: 6, w: 12, h: 12, rx: 2 }],
  speaker: [p('M11 5 6.5 9H4v6h2.5L11 19z'), p('M15.5 9.5a3.5 3.5 0 0 1 0 5M18 7a7 7 0 0 1 0 10')],
  headphones: [p('M4 14v-2a8 8 0 0 1 16 0v2'), p('M4 14h3v6H5.5A1.5 1.5 0 0 1 4 18.5zM20 14h-3v6h1.5a1.5 1.5 0 0 0 1.5-1.5z')],
  waveform: [p('M4 10v4M8 7v10M12 4v16M16 8v8M20 11v2')],

  // ── Chrome ──────────────────────────────────────────────
  search: [{ k: 'circle', cx: 11, cy: 11, r: 7 }, p('m20 20-3.2-3.2')],
  bell: [p('M18 8a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8'), p('M13.7 21a2 2 0 0 1-3.4 0')],
  message: [p('M4 5.5h16v11H9l-4.5 3v-3H4z'), p('M8 9h8M8 12.5h5')],
  mail: [{ k: 'rect', x: 3, y: 5, w: 18, h: 14, rx: 2.5 }, p('m3.5 7 8.5 6 8.5-6')],
  send: [p('M21 4 3 11l7 3 3 7z'), p('M10 14 21 4')],
  user: [{ k: 'circle', cx: 12, cy: 8, r: 3.5 }, p('M5 20a7 7 0 0 1 14 0')],
  users: [
    { k: 'circle', cx: 9, cy: 8, r: 3.2 },
    p('M3 20a6 6 0 0 1 12 0'),
    p('M16 5.5a3.2 3.2 0 0 1 0 6M17 20a6 6 0 0 0-2-4.5'),
  ],
  settings: [
    { k: 'circle', cx: 12, cy: 12, r: 3 },
    p(
      'M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.5-2.3 1a7 7 0 0 0-1.7-1L14.5 3h-5l-.4 2.5a7 7 0 0 0-1.7 1l-2.3-1-2 3.5 2 1.5a7 7 0 0 0 0 2l-2 1.5 2 3.5 2.3-1a7 7 0 0 0 1.7 1l.4 2.5h5l.4-2.5a7 7 0 0 0 1.7-1l2.3 1 2-3.5-2-1.5a7 7 0 0 0 .1-1z',
    ),
  ],
  help: [{ k: 'circle', cx: 12, cy: 12, r: 9 }, p('M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7M12 17h.01')],
  disable: [{ k: 'circle', cx: 12, cy: 12, r: 9 }, p('M6.5 6.5 17.5 17.5')],
  accessibility: [
    { k: 'circle', cx: 12, cy: 12, r: 9 },
    { k: 'circle', cx: 12, cy: 7.2, r: 1.35 },
    p('M12 9.6v3.2'),
    p('M9.2 11.2h5.6'),
    p('M12 12.8l-2.2 4.4M12 12.8l2.2 4.4'),
  ],
  logout: [p('M15 4h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-3M10 8l-4 4 4 4M6 12h10')],
  filter: [p('M4 6h16M7 12h10M10 18h4')],
  more: [p('M6 12h.01M12 12h.01M18 12h.01')],
  plus: [p('M12 5v14M5 12h14')],
  close: [p('m6 6 12 12M18 6 6 18')],
  check: [p('M20 6 9 17l-5-5')],
  checkCircle: [{ k: 'circle', cx: 12, cy: 12, r: 9 }, p('m8 12 3 3 5-6')],
  alert: [{ k: 'circle', cx: 12, cy: 12, r: 9 }, p('M12 7.5v5M12 16h.01')],
  info: [{ k: 'circle', cx: 12, cy: 12, r: 9 }, p('M12 11v5M12 8h.01')],
  clock: [{ k: 'circle', cx: 12, cy: 12, r: 8 }, p('M12 8v4l2.5 1.5')],
  calendar: [{ k: 'rect', x: 4, y: 5, w: 16, h: 16, rx: 2.5 }, p('M4 10h16M9 3v4M15 3v4')],
  heart: [p('M12 20s-7-4.3-7-9a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 4.7-7 9-7 9z')],
  lock: [{ k: 'rect', x: 5, y: 10, w: 14, h: 10, rx: 2.5 }, p('M8 10V8a4 4 0 0 1 8 0v2')],
  unlock: [{ k: 'rect', x: 4, y: 8, w: 16, h: 12, rx: 2 }, p('M8 8V6a4 4 0 0 1 8 0')],
  eye: [p('M2.5 12S6 6 12 6s9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z'), { k: 'circle', cx: 12, cy: 12, r: 3 }],
  eyeOff: [p('M4 4l16 16'), p('M9.5 9.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-1.2'), p('M6.7 6.9C4.2 8.5 2.5 12 2.5 12s3.5 6 9.5 6c1.6 0 3-.4 4.2-1M9.8 6.3A9 9 0 0 1 12 6c6 0 9.5 6 9.5 6a17 17 0 0 1-2.7 3.4')],
  globe: [{ k: 'circle', cx: 12, cy: 12, r: 9 }, p('M3.5 9.5h17M3.5 14.5h17'), p('M12 3c-2.5 2.5-3.5 5.6-3.5 9s1 6.5 3.5 9c2.5-2.5 3.5-5.6 3.5-9s-1-6.5-3.5-9z')],
  upload: [p('M12 16V5M8 9l4-4 4 4'), p('M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3')],
  download: [p('M12 5v11M8 12l4 4 4-4'), p('M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3')],
  refresh: [p('M20 12a8 8 0 1 1-2.4-5.7'), p('M20 4v5h-5')],
  trash: [p('M5 7h14M10 7V5h4v2'), p('M6.5 7 7.5 20h9L17.5 7')],
  edit: [p('M4 20h4L18.5 9.5a2 2 0 0 0-2.8-2.8L5 17.2z'), p('M14 7l3 3')],
  external: [p('M14 5h5v5'), p('M19 5 11 13'), p('M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4')],
  award: [{ k: 'circle', cx: 12, cy: 9, r: 5 }, p('M9 13.5 8 21l4-2 4 2-1-7.5')],
  lightbulb: [p('M9.5 17h5M10 20h4'), p('M12 3a5.5 5.5 0 0 1 3.2 10c-.6.5-.9 1.1-.9 1.8h-4.6c0-.7-.3-1.3-.9-1.8A5.5 5.5 0 0 1 12 3z')],

  // ── Content & objects ───────────────────────────────────
  file: [p('M6 3h7l5 5v13H6z'), p('M13 3v5h5')],
  folder: [p('M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z')],
  archive: [{ k: 'rect', x: 3, y: 4, w: 18, h: 4, rx: 1.5 }, p('M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8'), p('M10 12h4')],
  tag: [p('M4 11V5a1 1 0 0 1 1-1h6l9 9-7 7z'), p('M8 8h.01')],
  pin: [p('M12 21s6-5.7 6-10a6 6 0 1 0-12 0c0 4.3 6 10 6 10z'), { k: 'circle', cx: 12, cy: 11, r: 2.4 }],
  link: [p('M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.2 1.2'), p('M14 10a4 4 0 0 0-5.7 0l-3 3A4 4 0 0 0 11 18.7l1.2-1.2')],
  image: [{ k: 'rect', x: 3, y: 5, w: 18, h: 14, rx: 2.5 }, { k: 'circle', cx: 9, cy: 10, r: 1.6 }, p('m4 17 5-4 4 3 3-2 4 3')],
  camera: [p('M4 8h3l1.5-2h7L17 8h3v11H4z'), { k: 'circle', cx: 12, cy: 13, r: 3.4 }],
  board: [{ k: 'rect', x: 3, y: 4, w: 18, h: 16, rx: 2.5 }, p('M3 9h18M9 9v11')],
  terminal: [{ k: 'rect', x: 3, y: 4, w: 18, h: 16, rx: 2 }, p('M7 9l3 3-3 3M13 15h4')],
  server: [{ k: 'rect', x: 3, y: 4, w: 18, h: 12, rx: 2 }, p('M8 20h8M12 16v4M7 8h.01M7 12h6')],
  flag: [p('M5 21V4'), p('M5 5h11l-1.5 3L16 11H5z')],

  // ── System ──────────────────────────────────────────────
  shield: [p('M12 3 5 6v5.5c0 4.3 3 7.7 7 9.5 4-1.8 7-5.2 7-9.5V6z'), p('m9 12 2 2 4-4')],
  sliders: [p('M4 7h10M18 7h2M4 17h4M12 17h8'), { k: 'circle', cx: 16, cy: 7, r: 2 }, { k: 'circle', cx: 10, cy: 17, r: 2 }],
  sun: [{ k: 'circle', cx: 12, cy: 12, r: 4 }, p('M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4')],
  moon: [p('M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z')],
  language: [p('M4 6h9M8.5 4v2'), p('M11 6c0 4-3 8-7 9'), p('M6.5 10c1 2.5 3 4.5 6 5.5'), p('M13 20l3.5-8 3.5 8M14.4 17h4.2')],
  keyboard: [{ k: 'rect', x: 3, y: 6, w: 18, h: 12, rx: 2.5 }, p('M7 10h.01M11 10h.01M15 10h.01M17 10h.01M7 14h10')],
  creditCard: [{ k: 'rect', x: 3, y: 5, w: 18, h: 14, rx: 2.5 }, p('M3 10h18M7 15h3')],
  bellOff: [p('M4 4l16 16'), p('M18 8a6 6 0 0 0-9.3-5M6.2 9.4C6 15.4 3 16 3 16h13'), p('M13.7 20a2 2 0 0 1-3.4 0')],
  wifi: [p('M4.5 9.5a12 12 0 0 1 15 0'), p('M7.5 13a8 8 0 0 1 9 0'), p('M10.5 16.5a4 4 0 0 1 3 0'), p('M12 20h.01')],
  repeat: [p('M4 9a4 4 0 0 1 4-4h9'), p('m14 2 3 3-3 3'), p('M20 15a4 4 0 0 1-4 4H7'), p('m10 22-3-3 3-3')],
  shuffle: [p('M4 6h3l10 12h3M4 18h3l3-3.6M14 8.6 17 6h3'), p('m18 3 3 3-3 3M18 15l3 3-3 3')],
  skip: [p('m6 5 9 7-9 7z'), p('M18 5v14')],
  volumeOff: [p('M11 5 6.5 9H4v6h2.5L11 19z'), p('m16 10 4 4M20 10l-4 4')],
  minus: [p('M5 12h14')],
  dragHandle: [p('M9 7h.01M9 12h.01M9 17h.01M15 7h.01M15 12h.01M15 17h.01')],

  // ── Teacher surface ─────────────────────────────────────
  robot: [{ k: 'rect', x: 6, y: 8, w: 12, h: 10, rx: 2 }, p('M12 4v4M9 13h.01M15 13h.01M9.5 16h5')],
  gamepad: [
    p('M7 15h10a4 4 0 0 0 0-8H7a4 4 0 0 0 0 8z'),
    p('M9 11v4M7 13h4'),
    p('M15.5 11.5h.01M17.2 13.5h.01'),
  ],
  bank: [p('M4 9h16M6 9v9M18 9v9M4 18h16M12 5l8 4H4l8-4z')],
  activity: [p('M4 12h3l2.5-6 3 12L15.5 12H20')],
  copy: [
    { k: 'rect', x: 8, y: 8, w: 12, h: 12, rx: 1.5 },
    p('M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1'),
  ],
  bolt: [p('M13 2 4 14h7l-1 8 9-12h-7z')],
  progress: [p('M4 18h16M7 14l3-4 3 2 4-6')],

  // ── Arrows & chevrons ───────────────────────────────────
  arrowRight: [p('M4 12h15M13 6l6 6-6 6')],
  arrowLeft: [p('M20 12H5M11 6l-6 6 6 6')],
  arrowUp: [p('M12 19V5M6 11l6-6 6 6')],
  arrowDown: [p('M12 5v14M6 13l6 6 6-6')],
  chevronRight: [p('m9 6 6 6-6 6')],
  chevronLeft: [p('m15 6-6 6 6 6')],
  chevronDown: [p('m7 10 5 5 5-5')],
  chevronUp: [p('m7 14 5-5 5 5')],
  maximize: [
    { k: 'rect', x: 5, y: 5, w: 14, h: 14, rx: 2 },
    p('M9 3h6M15 3v6M9 21h6M9 15v6M3 9h6M3 15h6M21 9h-6M21 15h-6'),
  ],
  minimize: [
    { k: 'rect', x: 5, y: 5, w: 14, h: 14, rx: 2 },
    p('M9 12h6M12 9v6'),
  ],
} as const;

export type IconName = keyof typeof ICONS;
